import React from 'react';
import { cx } from '../cx';
import { IconCheckCircle, IconWarningCircle, IconErrorCircle, IconInfoCircle, IconClose } from '../icons';

// D-09 §15.10 — Alert (.alert · .alert-<kind> · .ai · .alert-body · .alert-action)
// 제목·닫기를 소비처가 인라인으로 만들지 않는다 — title / onClose 를 쓴다(§15.10 규칙).
export interface AlertProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  kind?: 'success' | 'warning' | 'error' | 'info';
  /** 미지정 시 kind 별 기본 아이콘. null 을 주면 아이콘 없음 */
  icon?: React.ReactNode;
  /** 본문 위 제목 줄 */
  title?: React.ReactNode;
  /** 우측 액션 슬롯 */
  action?: React.ReactNode;
  /** 주면 우측에 닫기 버튼 노출 */
  onClose?: () => void;
  /** 닫기 버튼 접근성 레이블 */
  closeLabel?: string;
}

const DEFAULT_ICON = {
  success: IconCheckCircle,
  warning: IconWarningCircle,
  error: IconErrorCircle,
  info: IconInfoCircle,
} as const;

export function Alert({
  kind = 'info',
  icon,
  title,
  action,
  onClose,
  closeLabel = '안내 닫기',
  className = '',
  children,
  ...props
}: AlertProps) {
  const Fallback = DEFAULT_ICON[kind];
  // icon 미지정(undefined)이면 기본 아이콘, null 이면 아이콘 없음
  const node = icon === undefined ? <Fallback /> : icon;

  return (
    <div className={cx('alert', `alert-${kind}`, className)} {...props}>
      {/* 아이콘+본문 묶음 — 위쪽 정렬 + 위아래 7px(§15.10 정렬 규칙) */}
      <div className="alert-main">
        {node != null && <span className="ai">{node}</span>}
        <div className="alert-body">
          {title != null && <div className="alert-title">{title}</div>}
          {children}
        </div>
      </div>
      {(action != null || onClose) && (
        <div className="alert-action">
          {action}
          {onClose && (
            <button type="button" className="alert-close" aria-label={closeLabel} onClick={onClose}>
              <IconClose width={14} height={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
